import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { RefreshCw, Package, Wallet, Send, Loader2, Lock, LogOut, Trash2, Eraser, Download, Crown, DollarSign, Check, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const statusStyles = {
  paid: "bg-[#FF007F]/10 text-[#FF007F] border-[#FF007F]/30",
  initiated: "bg-white/5 text-white/60 border-white/10",
  unpaid: "bg-white/5 text-white/60 border-white/10",
  reseller: "bg-[#00F2EA]/10 text-[#00F2EA] border-[#00F2EA]/30",
  processing: "bg-[#FF5E00]/10 text-[#FF5E00] border-[#FF5E00]/30",
  pending: "bg-white/5 text-white/60 border-white/10",
  delivering: "bg-[#FF5E00]/10 text-[#FF5E00] border-[#FF5E00]/30",
  delivered: "bg-[#FF007F]/10 text-[#FF007F] border-[#FF007F]/30",
  failed: "bg-red-500/10 text-red-400 border-red-500/30",
};

const PW_KEY = "boostup_admin_pw";

const AdminLogin = ({ onUnlock }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/login", { password: pw });
      localStorage.setItem(PW_KEY, pw);
      onUnlock(pw);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-6 py-24">
        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="w-full max-w-sm bg-[#0A0A0A] border border-white/10 rounded-2xl p-8"
        >
          <div className="w-12 h-12 rounded-xl bg-[#FF007F]/10 grid place-items-center mb-6">
            <Lock className="w-5 h-5 text-[#FF007F]" />
          </div>
          <h1 className="font-display text-2xl font-black tracking-tighter mb-2">
            Admin access
          </h1>
          <p className="text-sm text-white/50 mb-6">
            Enter the admin password to manage orders.
          </p>
          <Input
            data-testid="admin-password-input"
            type="password"
            autoFocus
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Password"
            className="bg-transparent border-white/10 text-white focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F] mb-4"
          />
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={busy || !pw}
            className="w-full bg-neon text-white font-bold py-3 rounded-full hover:scale-[1.02] transition-transform glow-pink disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Unlock
          </button>
        </form>
      </main>
      <Footer />
    </div>
  );
};

export default function Admin() {
  const [pw, setPw] = useState(() => localStorage.getItem(PW_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [payoutReqs, setPayoutReqs] = useState([]);
  const [rowBusy, setRowBusy] = useState({});

  // Auto-verify existing password on mount
  useEffect(() => {
    if (!pw) {
      setLoading(false);
      return;
    }
    api
      .post("/admin/login", { password: pw })
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem(PW_KEY);
        setPw("");
      })
      .finally(() => setLoading(false));
  }, [pw]);

  const authHeaders = { headers: { "X-Admin-Password": pw } };

  const load = async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const [o, b, a, p] = await Promise.all([
        api.get("/admin/orders", authHeaders),
        api.get("/admin/mtp/balance", authHeaders).catch(() => ({ data: { ok: false } })),
        api.get("/admin/affiliates", authHeaders).catch(() => ({ data: [] })),
        api.get("/admin/payout-requests", authHeaders).catch(() => ({ data: [] })),
      ]);
      setOrders(o.data);
      setBalance(b.data);
      setAffiliates(a.data);
      setPayoutReqs(p.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired");
        logout();
      }
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const logout = () => {
    localStorage.removeItem(PW_KEY);
    setPw("");
    setAuthed(false);
    setOrders([]);
    setBalance(null);
  };

  const setBusy = (id, flag) =>
    setRowBusy((prev) => ({ ...prev, [id]: flag }));

  const sendToPanel = async (orderId) => {
    setBusy(orderId, "send");
    try {
      const r = await api.post(`/admin/orders/${orderId}/send`, null, authHeaders);
      toast.success(
        r.data.already_sent
          ? `Already sent (panel #${r.data.panel_order_id})`
          : `Sent to panel (order #${r.data.panel_order_id})`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send to panel");
    } finally {
      setBusy(orderId, null);
    }
  };

  const refreshPanel = async (orderId) => {
    setBusy(orderId, "refresh");
    try {
      const r = await api.post(`/admin/orders/${orderId}/refresh`, null, authHeaders);
      toast.success(`Panel status: ${r.data.panel_status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to refresh");
    } finally {
      setBusy(orderId, null);
    }
  };

  const deleteOrder = async (orderId) => {
    setBusy(orderId, "delete");
    try {
      await api.delete(`/admin/orders/${orderId}`, authHeaders);
      toast.success("Order deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally {
      setBusy(orderId, null);
    }
  };

  const [clearing, setClearing] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const syncStripe = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/sync-stripe", null, authHeaders);
      toast.success(
        r.data.recovered > 0
          ? `Synced ${r.data.recovered} missing order${r.data.recovered === 1 ? "" : "s"} from Stripe`
          : "Already up to date"
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };
  const clearOrders = async (scope) => {
    setClearing(true);
    try {
      const r = await api.post("/admin/orders/clear", { scope }, authHeaders);
      toast.success(`Deleted ${r.data.deleted} order${r.data.deleted === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear");
    } finally {
      setClearing(false);
    }
  };

  if (!authed) {
    return (
      <AdminLogin
        onUnlock={(p) => {
          setPw(p);
          setAuthed(true);
        }}
      />
    );
  }

  const markPayout = async (affiliateId, pending) => {
    const raw = window.prompt(
      `How much did you pay this affiliate? (max $${pending.toFixed(2)})`,
      pending.toFixed(2)
    );
    if (raw === null) return;
    const amt = parseFloat(raw);
    if (!amt || amt <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    try {
      await api.post(
        `/admin/affiliates/${affiliateId}/payout`,
        { amount: amt },
        authHeaders
      );
      toast.success(`Marked $${amt.toFixed(2)} as paid`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record payout");
    }
  };

  const approvePayout = async (reqId) => {
    setBusy(reqId, "approve");
    try {
      const r = await api.post(`/admin/payout-requests/${reqId}/approve`, null, authHeaders);
      toast.success(`Approved — $${r.data.paid.toFixed(2)} marked as paid`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to approve");
    } finally {
      setBusy(reqId, null);
    }
  };

  const declinePayout = async (reqId) => {
    const note = window.prompt("Reason for declining (optional, shown to affiliate):", "") ?? "";
    setBusy(reqId, "decline");
    try {
      await api.post(`/admin/payout-requests/${reqId}/decline`, { note }, authHeaders);
      toast.success("Request declined");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to decline");
    } finally {
      setBusy(reqId, null);
    }
  };


  const totalRevenue = orders
    .filter((o) => o.payment_status === "paid")
    .reduce((s, o) => s + (o.amount || 0), 0);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 lg:px-10 py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="uppercase text-xs font-bold tracking-[0.2em] text-[#FF007F] mb-3">
              Operations
            </div>
            <h1 className="font-display text-4xl font-black tracking-tighter">
              Orders <span className="text-gradient-neon">dashboard</span>
            </h1>
          </div>
          <div className="flex items-center gap-3 self-start md:self-auto flex-wrap">
            <button
              data-testid="admin-sync-stripe-btn"
              onClick={syncStripe}
              disabled={syncing}
              className="inline-flex items-center gap-2 bg-[#FF007F]/10 border border-[#FF007F]/30 text-[#FF007F] px-4 py-2.5 rounded-full hover:bg-[#FF007F]/20 transition text-sm disabled:opacity-50"
              title="Pull any missing paid orders directly from Stripe"
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Sync from Stripe
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="admin-clear-menu-btn"
                  disabled={clearing}
                  className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-white/70 px-4 py-2.5 rounded-full hover:bg-white/10 hover:text-white transition text-sm disabled:opacity-50"
                >
                  {clearing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Eraser className="w-4 h-4" />
                  )}
                  Clear
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#0A0A0A] border-white/10 text-white">
                <ClearItem
                  label="Clear unpaid / abandoned"
                  desc="Deletes checkout attempts that never completed payment."
                  onConfirm={() => clearOrders("unpaid")}
                  testId="clear-unpaid"
                />
                <ClearItem
                  label="Clear delivered orders"
                  desc="Deletes orders already fulfilled by the panel."
                  onConfirm={() => clearOrders("delivered")}
                  testId="clear-delivered"
                />
                <ClearItem
                  label="Clear ALL orders"
                  desc="Deletes every order including paid ones. This cannot be undone."
                  destructive
                  onConfirm={() => clearOrders("all")}
                  testId="clear-all"
                />
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              data-testid="admin-refresh-btn"
              onClick={load}
              className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-white px-4 py-2.5 rounded-full hover:bg-white/10 transition text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-white/70 px-4 py-2.5 rounded-full hover:bg-white/10 hover:text-white transition text-sm"
            >
              <LogOut className="w-4 h-4" />
              Lock
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Total orders</div>
            <div className="font-display text-3xl font-black">{orders.length}</div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Paid</div>
            <div className="font-display text-3xl font-black">
              {orders.filter((o) => o.payment_status === "paid").length}
            </div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">Revenue (paid)</div>
            <div className="font-display text-3xl font-black text-gradient-neon">
              ${totalRevenue.toFixed(2)}
            </div>
          </div>
          <div
            data-testid="admin-mtp-balance-card"
            className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6"
          >
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40 mb-2">
              <Wallet className="w-3.5 h-3.5" /> Panel balance
            </div>
            {balance?.ok ? (
              <div className="font-display text-3xl font-black">
                ${parseFloat(balance.balance).toFixed(2)}
                <span className="text-xs text-white/40 ml-2 font-sans font-normal">
                  {balance.currency}
                </span>
              </div>
            ) : (
              <div className="font-display text-lg text-white/50">
                {balance?.error || "—"}
              </div>
            )}
          </div>
        </div>

        {payoutReqs.filter((r) => r.status === "pending").length > 0 && (
          <div
            data-testid="admin-payout-requests"
            className="bg-[#0A0A0A] border border-[#FF5E00]/30 rounded-2xl overflow-hidden mb-6 glow-pink"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Wallet className="w-4 h-4 text-[#FF5E00]" />
                <h2 className="font-display text-lg font-black tracking-tight">
                  Payout requests
                </h2>
                <span className="text-xs text-[#FF5E00] ml-2 bg-[#FF5E00]/10 px-2 py-0.5 rounded-full font-bold">
                  {payoutReqs.filter((r) => r.status === "pending").length} pending
                </span>
              </div>
              <div className="text-xs text-white/40 hidden md:block">
                Awaiting:{" "}
                <span className="text-white font-bold">
                  $
                  {payoutReqs
                    .filter((r) => r.status === "pending")
                    .reduce((s, r) => s + r.amount, 0)
                    .toFixed(2)}
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Requested</TableHead>
                    <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Affiliate</TableHead>
                    <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Code</TableHead>
                    <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Amount</TableHead>
                    <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">CashApp</TableHead>
                    <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Note</TableHead>
                    <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payoutReqs
                    .filter((r) => r.status === "pending")
                    .map((r) => (
                      <TableRow
                        key={r.id}
                        data-testid={`payout-req-row-${r.id}`}
                        className="border-white/5 hover:bg-white/[0.02]"
                      >
                        <TableCell className="text-white/70 text-xs whitespace-nowrap">
                          {new Date(r.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-white">{r.affiliate_email}</TableCell>
                        <TableCell>
                          <span className="font-mono text-[#FF007F] bg-[#FF007F]/10 border border-[#FF007F]/30 px-2 py-0.5 rounded-full text-xs">
                            {r.affiliate_promo_code}
                          </span>
                        </TableCell>
                        <TableCell className="font-display text-lg text-gradient-neon font-black">
                          ${r.amount.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <button
                            data-testid={`copy-cashapp-${r.id}`}
                            onClick={() => {
                              navigator.clipboard.writeText(r.cashapp_tag);
                              toast.success("CashApp tag copied");
                            }}
                            className="font-mono text-white bg-white/5 border border-white/10 px-2 py-1 rounded-full text-xs hover:bg-white/10 transition"
                            title="Click to copy"
                          >
                            {r.cashapp_tag}
                          </button>
                        </TableCell>
                        <TableCell className="text-white/60 text-xs max-w-xs truncate" title={r.note || ""}>
                          {r.note || "—"}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-2 justify-end">
                            <button
                              data-testid={`approve-payout-${r.id}`}
                              disabled={rowBusy[r.id]}
                              onClick={() => approvePayout(r.id)}
                              className="inline-flex items-center gap-1.5 bg-neon text-white text-xs font-bold px-3 py-1.5 rounded-full glow-pink disabled:opacity-40 hover:scale-[1.03] transition"
                              title="Mark as paid — you've already sent the money on CashApp"
                            >
                              {rowBusy[r.id] === "approve" ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Check className="w-3.5 h-3.5" />
                              )}
                              Approve
                            </button>
                            <button
                              data-testid={`decline-payout-${r.id}`}
                              disabled={rowBusy[r.id]}
                              onClick={() => declinePayout(r.id)}
                              className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 text-white/70 text-xs font-bold px-3 py-1.5 rounded-full hover:bg-white/10 transition disabled:opacity-40"
                            >
                              {rowBusy[r.id] === "decline" ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <XIcon className="w-3.5 h-3.5" />
                              )}
                              Decline
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {affiliates.length > 0 && (
          <div
            data-testid="admin-top-affiliates"
            className="bg-[#0A0A0A] border border-white/10 rounded-2xl overflow-hidden mb-6"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-[#FF5E00]" />
                <h2 className="font-display text-lg font-black tracking-tight">
                  Top affiliates
                </h2>
                <span className="text-xs text-white/40 ml-2">
                  ({affiliates.length} total)
                </span>
              </div>
              <div className="text-xs text-white/40 hidden md:block">
                Total paid out:{" "}
                <span className="text-white font-bold">
                  ${affiliates.reduce((s, a) => s + a.total_paid, 0).toFixed(2)}
                </span>
                {" · "}Pending:{" "}
                <span className="text-gradient-neon font-bold">
                  ${affiliates.reduce((s, a) => s + a.pending_payout, 0).toFixed(2)}
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em] w-8">#</TableHead>
                    <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Affiliate</TableHead>
                    <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Code</TableHead>
                    <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Earned</TableHead>
                    <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Pending</TableHead>
                    <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Paid orders</TableHead>
                    <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Clicks</TableHead>
                    <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Conv.</TableHead>
                    <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {affiliates.map((a, i) => (
                    <TableRow
                      key={a.id}
                      data-testid={`affiliate-row-${a.id}`}
                      className="border-white/5 hover:bg-white/[0.02]"
                    >
                      <TableCell className="text-white/40 font-mono">
                        {i === 0 ? (
                          <Crown className="w-4 h-4 text-[#FF5E00]" />
                        ) : (
                          i + 1
                        )}
                      </TableCell>
                      <TableCell className="text-white">{a.email}</TableCell>
                      <TableCell>
                        <span className="font-mono text-[#FF007F] bg-[#FF007F]/10 border border-[#FF007F]/30 px-2 py-0.5 rounded-full text-xs">
                          {a.promo_code}
                        </span>
                      </TableCell>
                      <TableCell className="text-white font-bold">
                        ${a.total_earned.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {a.pending_payout > 0 ? (
                          <span className="text-gradient-neon font-bold">
                            ${a.pending_payout.toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-white/30">$0.00</span>
                        )}
                      </TableCell>
                      <TableCell className="text-white/80">{a.paid_orders}</TableCell>
                      <TableCell className="text-white/60">{a.clicks}</TableCell>
                      <TableCell className="text-white/60">
                        {a.conversion_rate.toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-right">
                        {a.pending_payout > 0 ? (
                          <button
                            data-testid={`payout-btn-${a.id}`}
                            onClick={() => markPayout(a.id, a.pending_payout)}
                            className="inline-flex items-center gap-1.5 bg-[#FF007F]/10 border border-[#FF007F]/40 text-[#FF007F] text-xs font-bold px-3 py-1.5 rounded-full hover:bg-[#FF007F]/20 transition"
                            title="Mark a payout as paid"
                          >
                            <DollarSign className="w-3 h-3" />
                            Pay out
                          </button>
                        ) : (
                          <span className="text-white/30 text-xs">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}


        <div
          data-testid="admin-orders-table-wrap"
          className="bg-[#0A0A0A] border border-white/10 rounded-2xl overflow-hidden"
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Created</TableHead>
                  <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Platform</TableHead>
                  <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Username</TableHead>
                  <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Package</TableHead>
                  <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Amount</TableHead>
                  <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Payment</TableHead>
                  <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Status</TableHead>
                  <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em]">Panel</TableHead>
                  <TableHead className="text-white/50 uppercase text-[10px] tracking-[0.2em] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.length === 0 && !loading && (
                  <TableRow className="border-white/10">
                    <TableCell colSpan={9} className="text-center py-16 text-white/40">
                      <Package className="w-8 h-8 mx-auto mb-3 opacity-40" />
                      No orders yet.
                    </TableCell>
                  </TableRow>
                )}
                {orders.map((o) => {
                  const sent = !!o.panel_order_id;
                  const isPaid = o.payment_status === "paid" || o.payment_status === "reseller";
                  const isTk = (o.platform || "instagram") === "tiktok";
                  return (
                    <TableRow
                      key={o.id}
                      data-testid={`admin-order-row-${o.id}`}
                      className="border-white/5 hover:bg-white/[0.02]"
                    >
                      <TableCell className="text-white/70 text-xs whitespace-nowrap">
                        {new Date(o.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`text-[10px] uppercase font-bold tracking-[0.15em] px-2.5 py-1 rounded-full border whitespace-nowrap ${
                            isTk
                              ? "bg-[#00F2EA]/10 text-[#00F2EA] border-[#00F2EA]/30"
                              : "bg-[#FF007F]/10 text-[#FF007F] border-[#FF007F]/30"
                          }`}
                        >
                          {isTk ? "TikTok" : "Instagram"}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-white">@{o.instagram_username}</TableCell>
                      <TableCell className="text-white/80 whitespace-nowrap">{o.package_label}</TableCell>
                      <TableCell className="text-white font-semibold">${o.amount.toFixed(2)}</TableCell>
                      <TableCell>
                        <span
                          className={`text-[10px] uppercase font-bold tracking-[0.15em] px-2.5 py-1 rounded-full border whitespace-nowrap ${
                            statusStyles[o.payment_status] || "bg-white/5 text-white/60 border-white/10"
                          }`}
                        >
                          {o.payment_status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`text-[10px] uppercase font-bold tracking-[0.15em] px-2.5 py-1 rounded-full border whitespace-nowrap ${
                            statusStyles[o.status] || "bg-white/5 text-white/60 border-white/10"
                          }`}
                        >
                          {o.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-white/70 text-xs">
                        {sent ? (
                          <div className="flex flex-col">
                            <span className="text-white">#{o.panel_order_id}</span>
                            <span className="text-white/50">{o.panel_status || "—"}</span>
                          </div>
                        ) : o.panel_error ? (
                          <span className="text-red-400" title={o.panel_error}>error</span>
                        ) : (
                          <span className="text-white/30">not sent</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-2 justify-end">
                          {!sent ? (
                            <button
                              data-testid={`send-panel-btn-${o.id}`}
                              disabled={!isPaid || rowBusy[o.id] === "send"}
                              onClick={() => sendToPanel(o.id)}
                              className="inline-flex items-center gap-1.5 bg-neon text-white text-xs font-bold px-3 py-1.5 rounded-full glow-pink disabled:opacity-40 disabled:cursor-not-allowed hover:scale-[1.03] transition"
                              title={!isPaid ? "Order must be paid first" : "Send to panel"}
                            >
                              {rowBusy[o.id] === "send" ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Send className="w-3.5 h-3.5" />
                              )}
                              Send
                            </button>
                          ) : (
                            <button
                              data-testid={`refresh-panel-btn-${o.id}`}
                              disabled={rowBusy[o.id] === "refresh"}
                              onClick={() => refreshPanel(o.id)}
                              className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 text-white text-xs font-bold px-3 py-1.5 rounded-full hover:bg-white/10 transition disabled:opacity-40"
                            >
                              {rowBusy[o.id] === "refresh" ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <RefreshCw className="w-3.5 h-3.5" />
                              )}
                              Sync
                            </button>
                          )}
                          <DeleteRowButton
                            orderId={o.id}
                            username={o.instagram_username}
                            isPaid={isPaid}
                            busy={rowBusy[o.id] === "delete"}
                            onConfirm={() => deleteOrder(o.id)}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}


const DeleteRowButton = ({ orderId, username, isPaid, busy, onConfirm }) => {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          data-testid={`delete-order-btn-${orderId}`}
          disabled={busy}
          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/5 border border-white/10 text-white/50 hover:text-red-400 hover:border-red-400/40 hover:bg-red-500/10 transition disabled:opacity-40"
          title="Delete this order from history"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#0A0A0A] border-white/10 text-white">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-xl font-black tracking-tighter">
            Delete order for @{username}?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-white/60">
            This removes the order from your history.{" "}
            {isPaid
              ? "This was a PAID order — deleting only removes it from your dashboard; the customer's payment and any panel delivery are not affected."
              : "This order was never paid."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid={`confirm-delete-${orderId}`}
            onClick={onConfirm}
            className="bg-red-500 text-white hover:bg-red-600"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};


const ClearItem = ({ label, desc, onConfirm, destructive, testId }) => {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <DropdownMenuItem
          data-testid={testId}
          onSelect={(e) => e.preventDefault()}
          className={`cursor-pointer focus:bg-white/5 ${destructive ? "text-red-400" : "text-white/80"}`}
        >
          {label}
        </DropdownMenuItem>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#0A0A0A] border-white/10 text-white">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-xl font-black tracking-tighter">
            {label}?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-white/60">{desc}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className={destructive ? "bg-red-500 text-white hover:bg-red-600" : "bg-neon text-white"}
          >
            Confirm
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
